import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ReactFlowProvider } from '@xyflow/react';
import { message, Spin } from 'antd';
import { useDispatch } from 'react-redux';
import { LBCanvas } from '@/pages/Logiblocks/LBCanvas';
import type {
  WorkflowDetail,
  WorkflowGraphWriteResult,
  WorkflowStats,
  WorkflowSummary,
} from '@/apis/logiblocksAPI';
import { AppRoutes } from '@/appRoutes';
import {
  listWorkflows,
  createDraftWorkflow,
  duplicateWorkflow,
  getWorkflow,
  getWorkflowStats,
  updateWorkflow,
  publishWorkflow,
  deleteWorkflow,
} from '@/apis/logiblocksAPI';
import { reactFlowToLBWorkflow } from '@/pages/Logiblocks/serialization';
import { LBWorkflow } from '@/proto/logiblocks/logiblocks';
import type { ReactFlowEdge, ReactFlowNode } from '@/pages/Logiblocks/types';
import useToken from '@/common/helper/useToken';
import type { AppDispatch } from '@/redux-toolkit/store';
import { fetchWorkflowTemplates } from '@/redux-toolkit/slice/workflowTemplatesSlice';
import LogiblockDashboard from './logiblockDashboard/LogiblockDashboard';
import { useUnpublishConfirmation } from './useUnpublishConfirmation';
import { useSpin } from '@/components/SpinContext';
import {
  LOADING_LABEL_STYLE,
  LOADING_SPINNER_SIZE,
  LOADING_STACK_STYLE,
} from './styles';

type ViewMode = 'list' | 'canvas' | 'loadingCanvas';

/**
 * Logiblocks top-level component
 */
export default function Logiblocks() {
  const location = useLocation();
  const navigate = useNavigate();
  const reduxDispatch = useDispatch<AppDispatch>();
  const [workflows, setWorkflows] = useState<WorkflowSummary[]>([]);
  const [workflowStats, setWorkflowStats] = useState<WorkflowStats | null>(
    null
  );
  const [isLoadingWorkflows, setIsLoadingWorkflows] = useState(true);
  const { showSpin, hideSpin } = useSpin();
  const [currentWorkflow, setCurrentWorkflow] = useState<WorkflowDetail | null>(
    null
  );
  const graphRevisionRef = useRef(0);
  useEffect(() => {
    graphRevisionRef.current = currentWorkflow?.graph_revision ?? 0;
  }, [currentWorkflow?.id, currentWorkflow?.graph_revision]);
  // Description to auto-send as the AI Builder's first message, set when
  // arriving from the Automations empty state's Generate/template flow.
  const [initialAgentPrompt, setInitialAgentPrompt] = useState<
    string | undefined
  >(undefined);
  const { getToken } = useToken();
  const { requestUnpublish, unpublishConfirmModal } =
    useUnpublishConfirmation();

  // Workflows whose duplicate is still in progress, so a second click can't
  // make a second copy. The ref blocks the repeat click; the state greys out
  // the button.
  const duplicatingIdsRef = useRef<Set<string>>(new Set());
  const [duplicatingIds, setDuplicatingIds] = useState<Set<string>>(new Set());

  // Derive view mode from URL
  const queryParams = new URLSearchParams(location.search);
  const viewParam = queryParams.get('view');
  const workflowIdParam = queryParams.get('workflow');

  // Determine current view mode based on URL and workflow state
  let currentViewMode: ViewMode = 'list';
  if (viewParam === 'canvas') {
    if (workflowIdParam && !currentWorkflow) {
      // If we have a workflow ID but no workflow loaded, we must be loading
      currentViewMode = 'loadingCanvas';
    } else {
      currentViewMode = 'canvas';
    }
  }

  const [totalPages, setTotalPages] = useState(0);
  const [maxPageSize, setMaxPageSize] = useState(0);
  const [page, setPage] = useState<number>(1);
  const [searchInList, setSerachInList] = useState('');
  const [period, setPeriod] = useState<string | null>('all_time');
  const pageSize = 50;
  /** Fetches the list of workflows from the API and updates state. */
  const fetchWorkflows = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setIsLoadingWorkflows(false);
      return;
    }

    if (!searchInList) {
      setIsLoadingWorkflows(true);
      showSpin();
    }

    let apiPeriod = period;
    let startDate: string | null = null;
    let endDate: string | null = null;
    if (period?.startsWith('{')) {
      try {
        const parsed = JSON.parse(period) as { start?: string; end?: string };
        if (parsed.start && parsed.end) {
          apiPeriod = 'custom';
          startDate = parsed.start;
          endDate = parsed.end;
        }
      } catch {
        // malformed JSON — treat as all_time
        apiPeriod = null;
      }
    }

    try {
      const [data, stats] = await Promise.all([
        listWorkflows(
          token,
          undefined,
          searchInList,
          apiPeriod,
          startDate,
          endDate
        ),
        getWorkflowStats(token, apiPeriod, startDate, endDate),
      ]);
      setWorkflows(data);
      setWorkflowStats(stats);
      // This state will store the total_count from the backend response when the API returns paginated data.
      setMaxPageSize(data?.length ?? 0);
      // Currently, pages are calculated on the frontend, page_count will be set in this state when the API returns paginated data.
      const totalPageCount = Math.ceil((data?.length || 0) / pageSize);
      setTotalPages(totalPageCount);
    } catch (error) {
      console.error('Failed to fetch workflows:', error);
    } finally {
      hideSpin();
      setIsLoadingWorkflows(false);
    }
  }, [getToken, searchInList, showSpin, hideSpin, period]);

  // Fetch workflows and templates on mount
  useEffect(() => {
    fetchWorkflows();
    const token = getToken();
    if (token) {
      reduxDispatch(fetchWorkflowTemplates(token));
    }
  }, [fetchWorkflows, getToken, reduxDispatch]);

  // Reset to page 1 if the current page falls outside the valid range
  useEffect(() => {
    if (totalPages > 0 && page > totalPages) {
      setPage(1);
    }
  }, [totalPages, page]);

  // Load workflow when URL changes (for deep linking and back button support)
  useEffect(() => {
    const loadWorkflowFromUrl = async () => {
      if (viewParam === 'canvas' && workflowIdParam) {
        // Check if workflow was passed via router state first
        const passedWorkflow = location.state?.workflow as
          WorkflowDetail | undefined;

        if (passedWorkflow && passedWorkflow.id === workflowIdParam) {
          // Use the workflow from state - no API call needed!
          setCurrentWorkflow(passedWorkflow);
          setInitialAgentPrompt(
            location.state?.initialAgentPrompt as string | undefined
          );
        } else {
          // No state, so fetch from API (for deep linking, refresh, back button, etc.)
          const token = getToken();
          if (token) {
            try {
              const workflow = await getWorkflow(token, workflowIdParam);
              setCurrentWorkflow(workflow);
            } catch (error) {
              console.error('Failed to load workflow from URL:', error);
              // If workflow fails to load, navigate back to list
              navigate(AppRoutes.LOGIBLOCKS);
            }
          }
        }
      } else if (viewParam !== 'canvas') {
        // Clear current workflow when returning to list
        setCurrentWorkflow(null);
        setInitialAgentPrompt(undefined);
      }
    };

    loadWorkflowFromUrl();
  }, [viewParam, workflowIdParam, getToken, navigate, location.state]);

  const handleNewWorkflow = () => {
    setCurrentWorkflow({
      id: '',
      name: 'New Workflow',
      is_draft: true,
      graph_revision: 0,
      workflow: LBWorkflow.create(),
    });
    navigate(`${AppRoutes.LOGIBLOCKS}?view=canvas`);
  };

  const handleWorkflowClick = (workflowId: string) => {
    // Navigate to canvas view with workflow ID in URL
    navigate(AppRoutes.LOGIBLOCKS_CANVAS(workflowId));
  };

  const handleBackToList = () => {
    // Navigate back to list view
    navigate(AppRoutes.LOGIBLOCKS);
  };

  // Persist the current workflow graph to the backend. Pass keepalive=true from
  // unload-time callers so the request survives the document's destruction.
  // Throws on failure so the auto-persist hook can surface the error to the user.
  // If the workflow has no id yet (new, unsaved), creates the record first.
  const handlePersistWorkflow = useCallback(
    async (
      nodes: ReactFlowNode[],
      edges: ReactFlowEdge[],
      opts?: { keepalive?: boolean }
    ): Promise<WorkflowGraphWriteResult> => {
      const token = getToken();
      if (!token) {
        throw new Error('Cannot persist workflow: no auth token available');
      }
      if (!currentWorkflow) {
        throw new Error('Cannot persist workflow: no workflow loaded');
      }

      let workflowId = currentWorkflow.id;
      if (!workflowId) {
        const response = await createDraftWorkflow(
          token,
          currentWorkflow.name ?? 'New Workflow'
        );
        workflowId = response.new_workflow.id;
        const savedWorkflow = { ...currentWorkflow, id: workflowId };
        setCurrentWorkflow(savedWorkflow);
        setWorkflows(response.all_workflows);
        navigate(AppRoutes.LOGIBLOCKS_CANVAS(workflowId), {
          replace: true,
          state: { workflow: savedWorkflow },
        });
      }

      const workflow = reactFlowToLBWorkflow(nodes, edges);
      const result = await updateWorkflow(
        token,
        workflowId,
        { workflow, expectedGraphRevision: graphRevisionRef.current },
        opts
      );
      graphRevisionRef.current = result.graph_revision;
      setCurrentWorkflow((current) =>
        current?.id === workflowId
          ? { ...current, graph_revision: result.graph_revision, workflow }
          : current
      );
      return result;
    },
    [getToken, currentWorkflow, navigate]
  );

  const handleChangeWorkflowName = async (
    workflowId: string,
    newName: string
  ) => {
    const token = getToken();
    if (!token) {
      console.error('Cannot change name: no authentication token available');
      return;
    }

    if (!currentWorkflow || currentWorkflow.id !== workflowId) {
      console.error('Cannot change name: workflow mismatch');
      return;
    }

    // Save old name for rollback if API call fails
    const oldName = currentWorkflow.name;

    // Optimistic update - update UI immediately
    setCurrentWorkflow({ ...currentWorkflow, name: newName });

    setWorkflows((prevWorkflows) =>
      prevWorkflows.map((wf) =>
        wf.id === workflowId ? { ...wf, name: newName } : wf
      )
    );

    try {
      // Update the backend
      await updateWorkflow(token, workflowId, { name: newName });
      console.log('Workflow name updated successfully');
    } catch (error) {
      // Rollback on error - only if the user is still viewing the same workflow
      setCurrentWorkflow((current) =>
        current?.id === workflowId ? { ...current, name: oldName } : current
      );

      setWorkflows((prevWorkflows) =>
        prevWorkflows.map((wf) =>
          wf.id === workflowId ? { ...wf, name: oldName } : wf
        )
      );

      console.error('Failed to update workflow name:', error);
      // TODO: Show user-friendly error notification (e.g., toast message)
    }
  };

  const handlePublishWorkflow = async (
    workflowId: string,
    nodes: ReactFlowNode[],
    edges: ReactFlowEdge[]
  ) => {
    const token = getToken();
    if (!token) {
      console.error('Cannot publish: no authentication token available');
      return;
    }

    if (!currentWorkflow || currentWorkflow.id !== workflowId) {
      console.error('Cannot publish: workflow mismatch');
      return;
    }

    // Save old is_draft state for rollback if API call fails
    const oldIsDraft = currentWorkflow.is_draft;

    // Optimistic update - update UI immediately
    setCurrentWorkflow({ ...currentWorkflow, is_draft: false });

    setWorkflows((prevWorkflows) =>
      prevWorkflows.map((wf) =>
        wf.id === workflowId ? { ...wf, is_draft: false } : wf
      )
    );

    try {
      // Publish the workflow. We send the latest version of the workflow
      // so publish is really "save and publish".
      const workflow = reactFlowToLBWorkflow(nodes, edges);
      const result = await publishWorkflow(
        token,
        workflowId,
        graphRevisionRef.current,
        workflow
      );
      graphRevisionRef.current = result.graph_revision;
      setCurrentWorkflow((current) =>
        current?.id === workflowId
          ? { ...current, graph_revision: result.graph_revision }
          : current
      );
      setWorkflows((previous) =>
        previous.map((workflowSummary) =>
          workflowSummary.id === workflowId
            ? {
                ...workflowSummary,
                graph_revision: result.graph_revision,
              }
            : workflowSummary
        )
      );
      console.log('Workflow published successfully');
    } catch (error) {
      // Rollback on error - only if the user is still viewing the same workflow
      setCurrentWorkflow((current) =>
        current?.id === workflowId
          ? { ...current, is_draft: oldIsDraft }
          : current
      );

      setWorkflows((prevWorkflows) =>
        prevWorkflows.map((wf) =>
          wf.id === workflowId ? { ...wf, is_draft: oldIsDraft } : wf
        )
      );

      console.error('Failed to publish workflow:', error);
      message.error('Failed to publish workflow. Please try again.');
    }
  };

  const handleUnpublishWorkflow = async (workflowId: string) => {
    if (!currentWorkflow || currentWorkflow.id !== workflowId) {
      console.error('Cannot unpublish: workflow mismatch');
      return;
    }

    // Mark workflow as a draft only after the backend unpublishes it. Otherwise the
    // toggle reads Draft while the confirmation dialog is still open.
    await requestUnpublish(workflowId, () => {
      setCurrentWorkflow((current) =>
        current?.id === workflowId ? { ...current, is_draft: true } : current
      );

      setWorkflows((prevWorkflows) =>
        prevWorkflows.map((wf) =>
          wf.id === workflowId ? { ...wf, is_draft: true } : wf
        )
      );
    });
  };

  const handleToggleWorkflowLiveness = async (
    workflowId: string,
    currentlyDraft: boolean
  ) => {
    // Publishing updates the list up front. Unpublishing cannot, because the
    // user may still back out of the confirmation dialog.
    if (!currentlyDraft) {
      await requestUnpublish(workflowId, () => {
        setWorkflows((prev) =>
          prev.map((wf) =>
            wf.id === workflowId ? { ...wf, is_draft: true } : wf
          )
        );
      });
      return;
    }

    const token = getToken();
    if (!token) return;

    setWorkflows((prev) =>
      prev.map((wf) => (wf.id === workflowId ? { ...wf, is_draft: false } : wf))
    );

    try {
      const summary = workflows.find((workflow) => workflow.id === workflowId);
      if (!summary) {
        throw new Error(
          'Workflow revision is unavailable; refresh and try again.'
        );
      }
      const result = await publishWorkflow(
        token,
        workflowId,
        summary.graph_revision
      );
      setWorkflows((previous) =>
        previous.map((workflowSummary) =>
          workflowSummary.id === workflowId
            ? {
                ...workflowSummary,
                graph_revision: result.graph_revision,
              }
            : workflowSummary
        )
      );
    } catch (error) {
      setWorkflows((prev) =>
        prev.map((wf) =>
          wf.id === workflowId ? { ...wf, is_draft: true } : wf
        )
      );
      message.error(
        error instanceof Error
          ? error.message
          : 'Failed to update workflow status.'
      );
    }
  };

  // Copy a workflow into a new draft and refresh the list to show it.
  const handleDuplicateWorkflow = async (workflowId: string) => {
    const token = getToken();
    if (!token) {
      return;
    }
    if (duplicatingIdsRef.current.has(workflowId)) {
      return;
    }

    duplicatingIdsRef.current.add(workflowId);
    setDuplicatingIds(new Set(duplicatingIdsRef.current));

    try {
      const response = await duplicateWorkflow(token, workflowId);
      setWorkflows(response.all_workflows);
      message.success(`Workflow duplicated: ${response.new_workflow.name}`);
    } catch (error) {
      message.error(
        error instanceof Error ? error.message : 'Failed to duplicate workflow.'
      );
    } finally {
      duplicatingIdsRef.current.delete(workflowId);
      setDuplicatingIds(new Set(duplicatingIdsRef.current));
    }
  };

  const handleDeleteWorkflow = async (workflowId: string) => {
    const token = getToken();
    if (!token) {
      console.error('Cannot delete: no authentication token available');
      return;
    }

    // Remove workflow from list immediately
    setWorkflows((prevWorkflows) =>
      prevWorkflows.filter((wf) => wf.id !== workflowId)
    );

    try {
      // Delete the workflow
      await deleteWorkflow(token, workflowId);
      console.log('Workflow deleted successfully');

      // Only navigate back to list if the deleted workflow is still the active one
      if (currentWorkflow?.id === workflowId) {
        navigate(AppRoutes.LOGIBLOCKS);
      }
    } catch (error) {
      console.error('Failed to delete workflow:', error);
      // Note: We don't restore the workflow to the list since the user has already
      // left the workflow view. They can refresh to see the correct state.
    }
  };

  if (currentViewMode === 'list') {
    return (
      // <LBWorkflowList
      //   workflows={workflows}
      //   loading={isLoadingWorkflows}
      //   onNewWorkflow={handleNewWorkflow}
      //   onWorkflowClick={handleWorkflowClick}
      // />
      <>
        <LogiblockDashboard
          workflows={workflows}
          stats={workflowStats}
          onWorkflowClick={handleWorkflowClick}
          maxPageSize={maxPageSize}
          totalPages={totalPages}
          pageSize={pageSize}
          page={page}
          setPage={setPage}
          setSerachInList={setSerachInList}
          onNewWorkflow={handleNewWorkflow}
          loading={isLoadingWorkflows}
          period={period}
          setPeriod={setPeriod}
          onToggleLiveness={handleToggleWorkflowLiveness}
          onDuplicateWorkflow={handleDuplicateWorkflow}
          duplicatingWorkflowIds={duplicatingIds}
        />
        {unpublishConfirmModal}
      </>
    );
  }

  // Loading state: workflow is being fetched
  if (currentViewMode === 'loadingCanvas') {
    return (
      <div style={{ ...LOADING_STACK_STYLE, height: '100vh' }}>
        <Spin size={LOADING_SPINNER_SIZE} />
        <p style={LOADING_LABEL_STYLE}>Loading workflow...</p>
      </div>
    );
  }

  // Error state: canvas view without a workflow (and not loading)
  // This handles cases where workflow ID is missing or invalid
  if (currentViewMode === 'canvas' && !currentWorkflow) {
    console.error('Canvas view rendered without a workflow');
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2>Error: No workflow loaded</h2>
        <p>Unable to load the workflow editor.</p>
        <button onClick={handleBackToList}>Back to Workflow List</button>
      </div>
    );
  }

  return (
    <ReactFlowProvider>
      <LBCanvas
        workflow={currentWorkflow}
        onPersistWorkflow={handlePersistWorkflow}
        onChangeWorkflowName={handleChangeWorkflowName}
        onBack={handleBackToList}
        onPublishWorkflow={handlePublishWorkflow}
        onUnpublishWorkflow={handleUnpublishWorkflow}
        onDeleteWorkflow={handleDeleteWorkflow}
        initialAgentPrompt={initialAgentPrompt}
      />
      {unpublishConfirmModal}
    </ReactFlowProvider>
  );
}
